/**
 * ContactManager — inline contact management on VendorDetail page.
 *
 * Displays vendor contacts with add/edit/delete capabilities and
 * primary contact designation.
 *
 * Requirements: 25.1, 25.2, 25.3, 25.4, 25.5, 25.6, 25.7
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorService, type CreateContactInput, type UpdateContactInput } from '@/services/vendorService';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { VendorContact } from '@/types';

const PREDEFINED_ROLES = [
  'Sales Rep',
  'Account Manager',
  'Support Contact',
  'Billing Contact',
  'Legal Contact',
  'Technical Contact',
];

interface ContactManagerProps {
  vendorId: number;
  contacts: VendorContact[];
  canWrite: boolean;
}

interface ContactFormState {
  contactName: string;
  email: string;
  phone: string;
  contactRole: string;
  customRole: string;
  notes: string;
  isPrimary: boolean;
}

const emptyForm: ContactFormState = {
  contactName: '',
  email: '',
  phone: '',
  contactRole: PREDEFINED_ROLES[0],
  customRole: '',
  notes: '',
  isPrimary: false,
};

export function ContactManager({ vendorId, contacts, canWrite }: ContactManagerProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<VendorContact | null>(null);
  const [form, setForm] = useState<ContactFormState>(emptyForm);
  const [useCustomRole, setUseCustomRole] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VendorContact | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });

  const createMutation = useMutation({
    mutationFn: (input: CreateContactInput) => vendorService.createContact(vendorId, input),
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ contactId, input }: { contactId: number; input: UpdateContactInput }) =>
      vendorService.updateContact(vendorId, contactId, input),
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (contactId: number) => vendorService.deleteContact(vendorId, contactId),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (contactId: number) =>
      vendorService.updateContact(vendorId, contactId, { isPrimary: true }),
    onSuccess: () => invalidate(),
  });

  function openCreate() {
    setEditingContact(null);
    setForm({ ...emptyForm, isPrimary: contacts.length === 0 });
    setUseCustomRole(false);
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(contact: VendorContact) {
    setEditingContact(contact);
    const isPredefined = PREDEFINED_ROLES.includes(contact.contactRole);
    setForm({
      contactName: contact.contactName,
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      contactRole: isPredefined ? contact.contactRole : 'Custom',
      customRole: isPredefined ? '' : contact.contactRole,
      notes: contact.notes ?? '',
      isPrimary: contact.isPrimary,
    });
    setUseCustomRole(!isPredefined);
    setFormError(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingContact(null);
    setForm(emptyForm);
    setFormError(null);
  }

  function handleSubmit() {
    const role = useCustomRole ? form.customRole.trim() : form.contactRole;
    if (!form.contactName.trim()) {
      setFormError('Contact name is required.');
      return;
    }
    if (!role) {
      setFormError('Contact role is required.');
      return;
    }

    const input = {
      contactName: form.contactName.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      contactRole: role,
      notes: form.notes.trim() || undefined,
      isPrimary: form.isPrimary,
    };

    if (editingContact) {
      updateMutation.mutate({ contactId: editingContact.contactId, input });
    } else {
      createMutation.mutate(input);
    }
  }

  return (
    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
          Contacts ({contacts.length})
        </h2>
        {canWrite && (
          <button
            onClick={openCreate}
            style={{
              padding: '0.3rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            + Add Contact
          </button>
        )}
      </div>

      {contacts.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No contacts yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {contacts.map((contact) => (
            <div
              key={contact.contactId}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem 0.75rem',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: contact.isPrimary ? '#eff6ff' : '#fff',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{contact.contactName}</span>
                  {contact.isPrimary && <StatusBadge label="Primary" variant="blue" />}
                  <StatusBadge label={contact.contactRole} variant="gray" />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.15rem' }}>
                  {contact.email && <span>{contact.email}</span>}
                  {contact.email && contact.phone && <span> · </span>}
                  {contact.phone && <span>{contact.phone}</span>}
                </div>
              </div>
              {canWrite && (
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  {!contact.isPrimary && (
                    <button
                      onClick={() => setPrimaryMutation.mutate(contact.contactId)}
                      title="Set as primary"
                      style={{
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                      }}
                    >
                      ★ Primary
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(contact)}
                    style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      fontSize: '0.7rem',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteTarget(contact)}
                    style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #fca5a5',
                      backgroundColor: '#fff',
                      color: '#dc2626',
                      cursor: 'pointer',
                      fontSize: '0.7rem',
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Contact Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={closeForm}
        title={editingContact ? 'Edit Contact' : 'Add Contact'}
        footer={
          <>
            <button
              onClick={closeForm}
              style={{ padding: '0.4rem 1rem', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
              style={{ padding: '0.4rem 1rem', borderRadius: '4px', border: 'none', backgroundColor: '#c8102e', color: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}
            >
              {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {formError && (
          <div style={{ marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: '#fef2f2', borderRadius: '4px', color: '#991b1b', fontSize: '0.8rem' }}>
            {formError}
          </div>
        )}
        <FormField label="Name" value={form.contactName} onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} required />
        <FormField label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        <FormField label="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />

        <FormField label="Role">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select
              value={useCustomRole ? 'Custom' : form.contactRole}
              onChange={(e) => {
                if (e.target.value === 'Custom') {
                  setUseCustomRole(true);
                } else {
                  setUseCustomRole(false);
                  setForm((f) => ({ ...f, contactRole: e.target.value }));
                }
              }}
              style={{ flex: 1, padding: '0.4rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
            >
              {PREDEFINED_ROLES.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
              <option value="Custom">Custom…</option>
            </select>
          </div>
        </FormField>

        {useCustomRole && (
          <FormField
            label="Custom Role"
            value={form.customRole}
            onChange={(e) => setForm((f) => ({ ...f, customRole: e.target.value }))}
            placeholder="Enter custom role name"
          />
        )}

        <FormField label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={2}
            style={{ width: '100%', padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.875rem', resize: 'vertical' }}
          />
        </FormField>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isPrimary}
            onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
          />
          Primary contact
        </label>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.contactId)}
        title="Delete Contact"
        message={`Are you sure you want to delete "${deleteTarget?.contactName}"?`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
